/**
 * Translations already seen, kept for the life of the tab.
 *
 * The component holds these in a Map so that flipping between modes on one piece
 * of text is instant. That Map dies with the page, which is a shame for the two
 * things people do most: reloading, and going away to another tab and coming
 * back. Both replay work that has already been paid for — the debounce, the
 * round trip, the quota — for an answer we still have.
 *
 * `sessionStorage` rather than `localStorage`: this is a scratch buffer for the
 * session in front of the real cache on the server, not a durable store, and it
 * should not follow someone into next week.
 */

// v2: the modes changed what they MEAN in 2026-08-07 — A1, Humanize and
// Mistakes now answer in the language you wrote in rather than in English — so
// an entry stored under v1 is an answer to a different question with an
// identical key. Bumping the name is how those get dropped instead of replayed.
//
// v3: script detection used to call a whole English paragraph Korean if a single
// Korean word appeared anywhere in it (see detectScript in the backend's
// lib/languages.ts). The modes that answer in the language you wrote in took
// that at its word and replied in Korean — and the reply was then CACHED, so
// fixing the detector would not have fixed what those visitors saw. The answers
// stored under v2 are answers to a question that was asked wrongly; this is how
// they get dropped rather than replayed.
const KEY = 'gt.results.v3';
/** Fewer entries than the in-memory Map holds — this one is serialised on write. */
const MAX = 40;
/**
 * The biggest key+value pair worth writing down.
 *
 * This used to be 4,000, on the reasoning that "a 5,000-character essay is not
 * what repeats" — which was true right up until the box started PAGING long
 * text. A page is up to 5,000 characters and its answer is about as long again,
 * so every single page of every long document fell over this line and was
 * quietly left out of the cache: the in-memory Map served them for the life of
 * the tab, and a reload paid for all ten pages a second time.
 *
 * A page of a long document is now the thing that repeats most — turning back
 * to page 1 is one click, and the pager invites it. 12,000 covers a full page
 * and its translation with room for the tags; forty of those is under half a
 * megabyte against a ~5MB quota, and the write already survives the quota being
 * hit anyway (see `saveResults`).
 */
const MAX_ENTRY_CHARS = 12_000;

export interface Restored {
  /** The model that produced these, so a model change can throw them away. */
  model: string | null;
  entries: [string, string][];
}

export function loadResults(): Restored {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return { model: null, entries: [] };
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.entries)) return { model: null, entries: [] };
    return {
      model: typeof parsed.model === 'string' ? parsed.model : null,
      entries: parsed.entries.filter(
        (e: unknown): e is [string, string] =>
          Array.isArray(e) && e.length === 2 && typeof e[0] === 'string' && typeof e[1] === 'string' &&
          // A blank answer is never worth replaying (see `remember` in Translator.tsx).
          e[1].trim() !== '',
      ),
    };
  } catch {
    // Private-mode quirks, a half-written value, a schema from an older build:
    // none of it is worth a broken page over a cache.
    return { model: null, entries: [] };
  }
}

let timer: ReturnType<typeof setTimeout> | null = null;

/**
 * Persist, on a trailing timer.
 *
 * Serialising the whole set is cheap but not free, and a result lands on every
 * finished translation — including each one a fast typist abandons. Waiting a
 * second collapses a burst into a single write and keeps it off the frames where
 * the answer is actually being painted.
 */
export function saveResults(model: string | null, entries: Iterable<[string, string]>): void {
  if (timer) clearTimeout(timer);
  const snapshot = [...entries]
    .filter(([k, v]) => k.length + v.length <= MAX_ENTRY_CHARS)
    .slice(-MAX);
  timer = setTimeout(() => {
    timer = null;
    try {
      sessionStorage.setItem(KEY, JSON.stringify({ model, entries: snapshot }));
    } catch {
      // Quota exceeded is the realistic failure. Drop what we have rather than
      // retrying into the same wall.
      try { sessionStorage.removeItem(KEY); } catch { /* nothing left to try */ }
    }
  }, 1000);
}
